//
// 03/05/2019 note:
// When changing any site settings, be sure to follow the process in the "Settings Support Work In-take Process" wiki page:
//    https://mytableau.tableaucorp.com/x/coDWD
//

import * as _ from 'underscore';

import { appStore } from '../../app/AppStore';
import { RemoteAgentPresenceIcon } from '../../app/content/RemoteAgentPresenceIcon';
import { NavLocationSelectors } from '../../app/selectors/NavigationStateSelectors';
import { ILocLinkifierModel } from '../../ts/filters/LocLinkifierFilter';
import { ActionContext } from '../../ts/helpers/ActionContext';
import { Module } from '../../ts/Module';
import { RemoteRefreshAgents } from '../../ts/resources/RemoteRefreshAgents';
import { Sites } from '../../ts/resources/Sites';
import { IRemoteAgentConnectionStatus } from '../../ts/ServerApi/requests/GetSiteRemoteAgentsConnectionStatusRequest';
import { IError } from '../../ts/ServerApi/types/IError';
import { IResult } from '../../ts/ServerApi/types/IResponse';
import { DeleteRemoteAgentAction } from '../../ts/services/DeleteRemoteAgentAction';
import { HelpService } from '../../ts/services/HelpService';
import { ServerService } from '../../ts/services/ServerService';
import { ISettingsCoordinatorCtrlScope, ISettingsFormScope, SettingsFormService } from '../directives/tbSettingsCoordinator';
import { IRemoteQueryAgent, ISiteBridgeSettingsForSiteAdmin } from '../interfaces/ISiteSettingsForSiteAdmin';

export interface IRemoteAgentType {
  name: string;
  pooled: boolean;
  poolName: string;
}

export interface ISiteSettingsForBridgeScope extends ISettingsCoordinatorCtrlScope {
  bridgeSettingsFormModel: ISiteBridgeSettingsForSiteAdmin;
  siteSettingsForBridgeForm: ng.IFormController; // the form name given in siteSettingsForBridgeForm.pug

  // Remote agents-related
  showRemoteAgentsSection: boolean;
  siteRemoteAgentSettingsLearnMoreHref?: string;
  remoteAgentPoolingLearnMoreHref: string;
  clientSummaryLearnMoreHref: string;
  remoteAgentTypes: IRemoteAgentType[];
  remoteAgentModeApplication: string;
  remoteAgentModeService: string;
  deleteRemoteAgent: (agent: IRemoteQueryAgent, $event: JQueryEventObject) => ng.IPromise<any>;
  remoteAgentVersionPopoverLinkifierModel: () => ILocLinkifierModel;

  siteId: string;
  remoteAgentPresenceIconProperties?: _.Dictionary<RemoteAgentPresenceIcon.Props>;
}

export class SiteSettingsForBridgeFormCtrl {

  private master: ISiteBridgeSettingsForSiteAdmin;
  private formState: ISettingsFormScope;

  private remoteAgentTypeExtactAndLive: IRemoteAgentType;
  private remoteAgentTypeExtract: IRemoteAgentType;

  public static $inject = [
    '$scope',
    '$q',
    '$translate',
    'SettingsFormService',
    'DeleteRemoteAgentAction',
    'HelpService',
    'RemoteRefreshAgents',
    'Sites',
    'ServerService',
  ];

  constructor(
    private scope: ISiteSettingsForBridgeScope,
    private $q: ng.IQService,
    private $translate: ng.translate.ITranslateService,
            formService: SettingsFormService,
    private deleteRemoteAgentAction: DeleteRemoteAgentAction,
    private help: HelpService,
    private remoteAgents: RemoteRefreshAgents,
    private sitesResource: Sites,
            server: ServerService,
  ) {
    this.scope.bridgeSettingsFormModel = scope.siteSettings as ISiteBridgeSettingsForSiteAdmin;

    // Setup localized strings
    this.scope.remoteAgentModeApplication = this.$translate.instant('remoteAgentModeApplication');
    this.scope.remoteAgentModeService = this.$translate.instant('remoteAgentModeService');

    this.remoteAgentTypeExtract = {name: this.$translate.instant('remoteAgentTypeExtract'),
                                   pooled: false,
                                   poolName: this.$translate.instant('siteRemoteAgentPoolNameNotAvailable')};
    this.remoteAgentTypeExtactAndLive = {name: this.$translate.instant('remoteAgentTypeExtactAndLive'),
                                         pooled: true,
                                         poolName: this.$translate.instant('siteRemoteAgentPoolNameDefault')};
    this.scope.remoteAgentTypes = [this.remoteAgentTypeExtract, this.remoteAgentTypeExtactAndLive];

    this.scope.showRemoteAgentsSection = server.isBridgeEnabled();

    if (this.scope.showRemoteAgentsSection) {
      const remoteAgentPresenceStrings: RemoteAgentPresenceIcon.StateTextStrings = {
        stateTextConnected: $translate.instant('remoteAgentConnectionStatus_connected'),
        stateTextNotConnected: $translate.instant('remoteAgentConnectionStatus_disconnected'),
        stateTextNotInServiceMode: $translate.instant('remoteAgentConnectionStatus_notInServiceMode'),
        stateTextNotResponsive: $translate.instant('remoteAgentConnectionStatus_notResponding'),
        stateTextNeedsUpgrade: $translate.instant('remoteAgentConnectionStatus_needsUpgrade'),
        stateTextThrottled: $translate.instant('remoteAgentConnectionStatus_throttled')
      };
      const currentStateStringMap: _.Dictionary<string> = {};
      currentStateStringMap['CONNECTED'] = $translate.instant('remoteAgentConnectionStatus_connected_short');
      currentStateStringMap['DISCONNECTED'] = $translate.instant('remoteAgentConnectionStatus_disconnected_short');
      currentStateStringMap['NEEDS_UPGRADE'] = $translate.instant('remoteAgentConnectionStatus_needsUpgrade_short');
      currentStateStringMap['NOT_IN_SERVICE_MODE'] = $translate.instant('remoteAgentConnectionStatus_notInServiceMode_short');
      currentStateStringMap['NOT_RESPONSIVE'] = $translate.instant('remoteAgentConnectionStatus_notResponding_short');
      currentStateStringMap['THROTTLED'] = $translate.instant('remoteAgentConnectionStatus_throttled_short');

      this.remoteAgents.getSiteRemoteAgentsConnectionStatus().then(
        (statusList: IRemoteAgentConnectionStatus[]) => {
          const presenceProps: _.Dictionary<RemoteAgentPresenceIcon.Props> = {};
          statusList.forEach((status: IRemoteAgentConnectionStatus) => {
            presenceProps[status.agentName] = {
              angularContext: null,
              currentState: status.connectionStatus,
              currentStateString: currentStateStringMap[status.connectionStatus],
              stateTextStrings: remoteAgentPresenceStrings
            };
          });
          this.scope.remoteAgentPresenceIconProperties = presenceProps;
        }
      );
      this.scope.siteRemoteAgentSettingsLearnMoreHref = this.help.getServerHelpUrlByCategory('bridge-live-query');
      this.scope.remoteAgentPoolingLearnMoreHref = this.help.getServerHelpUrlByCategory('bridge-pooling');
      this.scope.clientSummaryLearnMoreHref = this.help.getServerHelpUrlByCategory('bridge-settings-summary');

      this.scope.remoteAgentVersionPopoverLinkifierModel = () => {
        return {
          localizedStatementKey: 'remoteAgentVersionPopoverDetail',
          links: [{
            key: '{TABLEAU_BRIDGE_RELEASES}',
            localizedValueKey: 'remoteAgentVersionBridgeReleases',
            url: this.help.getServerHelpUrlByCategory('bridge-release')
          }]
        };
      };
    }

    this.scope.sharedForm = formService;
    this.scope.localForm
      ? this.scope.localForm.formController = this.scope.siteSettingsForBridgeForm
      : this.scope.localForm = {
        formController: this.scope.siteSettingsForBridgeForm,
        cancel: () => this.resetForm(),
        isBusy: () => this.scope.localForm.busy,
        isFormUnchanged: () => angular.equals(this.master, this.scope.bridgeSettingsFormModel),
        isValid: () => this.formState.formController.isValid(this.scope.siteSettingsForBridgeForm),
        save: () => this.getSiteBridgeSettingsForSiteAdminUpdatePromise(),
      };

    this.master = angular.copy(this.scope.bridgeSettingsFormModel);

    const refreshRemoteAgents = () => this.sitesResource.getCurrentSiteBridgeSettingsForSiteAdmin()
      .then((siteBridgeSettingsForSiteAdmin: ISiteBridgeSettingsForSiteAdmin) => this.scope.bridgeSettingsFormModel = siteBridgeSettingsForSiteAdmin);

    this.scope.deleteRemoteAgent = (agent: IRemoteQueryAgent, $event: JQueryEventObject) =>
      this.deleteRemoteAgentAction.execute(agent, agent.ownerId, ActionContext.fromEvent($event))
      .then(refreshRemoteAgents);
  }

  private resetForm(){
    // todo - use the shared logic in tbSettingsCoordinator instead
    //this.scope.sharedForm.revertFormValues(this.scope.bridgeSettingsFormModel,this.scope.siteSettingsForBridgeForm, this.master);
    this.scope.bridgeSettingsFormModel = angular.copy(this.master);
    // TODO: Use Angular 1.3 $validators/$validate and pull validation out of $parsers/$formatters
    _.each(this.formState.formController.$error, (error: ng.INgModelController[], key: string) => {
      if (error) {
        error[0].$setValidity(key, true);
      }
    });
  }

  // TODO: move the saving logic into SitesResource. Error handling should be done by users of this form
  private getSiteBridgeSettingsForSiteAdminUpdatePromise(): ng.IPromise<any> {
    const site = <any>angular.copy(this.scope.bridgeSettingsFormModel);

    this.scope.localForm.busy = true;
    let errors: IError[] = [];
    return this.sitesResource.updateSiteBridgeSettingsForSiteAdmin(site.id, site.siteAdminBridgeSettings).then((result: IResult) => {
      if (result.errors && result.errors.length) {
        errors = errors.concat(result.errors);
        this.formState.updateFailure(errors, null);
        return this.$q.reject();
      } else {

        this.updateSuccess(site);
      }
    })
    .finally(() => this.scope.localForm.busy = false);
  }

  // Success/Error Handlers

  private updateSuccess(site: ISiteBridgeSettingsForSiteAdmin): void {
    // Only need to update current view and state if we're in server or site settings
    if (!NavLocationSelectors.inSettingsLocation(appStore.getState())) {
      return;
    }
    this.master = angular.copy(site);
  }


}


Module.instance().controller('SiteSettingsForBridgeFormCtrl', SiteSettingsForBridgeFormCtrl);
